// Sentry cargado con import(), no en el arranque.
//
// LDT-718 · `@sentry/react` son ~160 KB comprimidos (core, browser, tracing y
// el replay) y viajaba en el trozo de entrada que descarga la landing. La
// configuración sigue en src/main.tsx (sus tests la leen de ahí); aquí solo se
// decide CUÁNDO se carga:
//   · en la app (/platform, /app…), en cuanto arranca, en paralelo al chunk de
//     la ruta: los errores del TPV se siguen viendo desde el primer momento,
//     con el tracing y el replay de siempre;
//   · en la landing, solo si hace falta: la primera vez que hay un error (de
//     React, o uno que llegue a window). Se carga, se inicializa y se envía.
//     Una visita sin errores no descarga Sentry. Lo que se pierde es el
//     tracing de rendimiento de la landing (10 % de muestreo), que ya mide
//     Vercel Speed Insights con consentimiento.
// Lo que quiera usar Sentry antes de que esté listo espera a `whenSentryReady`.
type SentryModule = typeof import("@sentry/react");

let ready: Promise<SentryModule> | null = null;

let pendingInit: ((Sentry: SentryModule) => void) | null = null;
let initialized = false;

function load(init: (Sentry: SentryModule) => void): Promise<SentryModule> {
  ready = import("@sentry/react").then((Sentry) => {
    init(Sentry);
    initialized = true;
    return Sentry;
  });
  // Si el chunk no llega (sin red, bloqueador), nadie más debe romperse por ello.
  ready.catch(() => undefined);
  return ready;
}

/**
 * Carga Sentry y llama a `init` con el módulo (que hace el `Sentry.init`).
 * @param onlyOnError en la landing: no cargar nada hasta el primer error.
 */
export function initSentry(init: (Sentry: SentryModule) => void, { onlyOnError }: { onlyOnError: boolean }): void {
  if (!onlyOnError) {
    void load(init);
    return;
  }
  pendingInit = init;
  // Los errores que no pasan por React: se cargan Sentry y se reenvían. Sus
  // propios manejadores globales solo existen tras el init, así que estos
  // primeros los mandamos a mano (el `ignoreErrors` del init los filtra igual).
  // Una vez inicializado, los captura el propio Sentry: aquí ya no, o irían dos veces.
  window.addEventListener("error", (e) => {
    if (!initialized) captureException(e.error ?? e.message);
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (!initialized) captureException(e.reason);
  });
}

/** Carga Sentry ya si estaba esperando a un error: al entrar en la app desde
 *  la landing sin recargar, el TPV recupera su Sentry de siempre. */
export function loadSentryNow(): void {
  if (!ready && pendingInit) {
    const init = pendingInit;
    pendingInit = null;
    void load(init);
  }
}

/** Resuelve con Sentry ya inicializado; `null` si aún no se ha cargado (en la
 *  landing, mientras no haya habido errores) o no pudo cargarse. */
export function whenSentryReady(): Promise<SentryModule | null> {
  return ready ? ready.catch(() => null) : Promise.resolve(null);
}

/** `Sentry.captureException` sin importar Sentry en el trozo de quien llama.
 *  En la landing, el primer error es el que carga Sentry. */
export function captureException(error: unknown, hint?: Parameters<SentryModule["captureException"]>[1]): void {
  loadSentryNow();
  void whenSentryReady().then((Sentry) => Sentry?.captureException(error, hint));
}
